<template>
	<div class="printer-category-setting-container">
		<ModalHeader :title="selectedMenuName" :button-text="getString('save')" @click-button="saveCategories" />
		<div class="alignment-row row-select-menu">
			<p>{{ getString('menu_select', [userStore.menuWord]) }}</p>
			<AppSelect
				class="right-element"
				:title="getString('select')"
				:values="selectableMenus"
				:selected-index="selectedMenuIndex"
				@selected="changeMenu"
			/>
		</div>
		<AppList :items="sortedCategoriesItems">
			<template v-slot:checklist-category="{ item }">
				<div class="alignment-row">
					<p>{{ getString(item.title) }}</p>
					<AppSelect
						class="right-element"
						:title="getString('select')"
						:values="printTypes"
						:selected-index="item.printTypeIndex"
						@selected="type => changePrintType(item.categoryId, type)"
					/>
				</div>
			</template>
		</AppList>
	</div>
</template>

<script>
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import {useLanguageStore, useNotificationStore} from "@/shared/stores";
import {useMenuStore, usePrintersStore, useUserStore} from "@/console-new/stores";
import AppButton from "@/shared/components/AppButton.vue";
import AppList from "@/shared/components/AppList.vue";
import AppChecklist from "@/shared/components/AppChecklist.vue";
import AppSelect from "@/shared/components/AppSelect.vue";

export default {
	setup() {
		const languageStore = useLanguageStore();
		const menuStore = useMenuStore();
		const userStore = useUserStore();
		const notificationStore = useNotificationStore();
		const printersStore = usePrintersStore();
		return {
			getString: languageStore.getString,
			menuStore,
			userStore,
			showNotification: notificationStore.showNotification,
			printersStore,
		}
	},
	name: "PrinterCategorySettingEditor",
	emits: ["close"],
	components: {AppSelect, AppChecklist, AppList, AppButton, ModalHeader},
	data() {
		return {
			selectedMenu: null,
		}
	},
	props: {
		printerId: {
			type: Number,
			required: true,
		},
	},
	computed: {
		printer() {
			return this.printersStore.businessPrinters ? this.printersStore.businessPrinters.find(printer => printer.id === this.printerId) : {};
		},
		selectedMenuIndex() {
			let index = 0;
			for(let i = 0; i < this.sortedMenus.length; i++) {
				if(this.sortedMenus[i].menu_id === this.menuStore.modMenu.menu_id) {
					index = i;
					break;
				}
			}
			return index;
		},
		selectedMenuName() {
			return this.menuStore.modMenu.name || this.userStore.menuWord;
		},
		selectableMenus() {
			return this.sortedMenus.map(r => r.name || this.userStore.menuWord);
		},
		sortedMenus() {
			return this.menuStore.sortedMenus;
		},
		sortedCategories() {
			return Object.values(this.menuStore.modMenu.categories).sort((a, b) => a.order_number - b.order_number);
		},
		sortedCategoriesItems() {
			return this.sortedCategories.map(r => {
				let printTypeIndex = 0;
				if(this.categoriesExcludedMap[r.menu_category_id]) {
					printTypeIndex = 1;
				}
				if(this.categoriesSmallMap[r.menu_category_id]) {
					printTypeIndex = 2;
				}
				return {
					title: r.name || this.getString("no_name"),
					customSlot: "checklist-category",
					printTypeIndex: printTypeIndex,
					categoryId: r.menu_category_id,
				}
			});
		},
		categoriesExcludedMap() {
			let excludedMap = {};
			this.printer.categoriesExcl.forEach(r => {
				excludedMap[r] = r;
			});
			return excludedMap;
		},
		categoriesSmallMap() {
			let smallMap = {};
			this.printer.categoriesSmall.forEach(r => {
				smallMap[r] = r;
			});
			return smallMap;
		},
		printTypes() {
			return [
				this.getString("print"),
				this.getString("dont_print"),
				this.getString("print_small"),
			];
		},
	},
	methods: {
		changeMenu(i) {
			let menus = this.sortedMenus;
			if(i < menus.length) {
				let selectedMenu = menus[i];
				if(selectedMenu) {
					this.menuStore.changeModMenu(selectedMenu.menu_id);
				}
			}
		},
		changePrintType(categoryId, newType) {
			if(newType === 0) {
				this.printer.categoriesExcl = this.printer.categoriesExcl.filter(r => r !== categoryId);
				this.printer.categoriesSmall = this.printer.categoriesSmall.filter(r => r !== categoryId);
			} else if(newType === 1) {
				let findIndex = this.printer.categoriesExcl.findIndex(r => r === categoryId);
				if(findIndex === -1) {
					this.printer.categoriesExcl.push(categoryId);
				}
				this.printer.categoriesSmall = this.printer.categoriesSmall.filter(r => r !== categoryId);
			} else if(newType === 2) {
				let findIndex = this.printer.categoriesSmall.findIndex(r => r === categoryId);
				if(findIndex === -1) {
					this.printer.categoriesSmall.push(categoryId);
				}
				this.printer.categoriesExcl = this.printer.categoriesExcl.filter(r => r !== categoryId);
			}
		},
		saveCategories() {
			this.printersStore.updatePrinterExcludedCategories(this.printer.id, this.printer.categoriesExcl, this.printer.categoriesSmall, this.printer.usersExcl, catSuccess => {
				this.showNotification(catSuccess ? 'generic-success' : 'generic-fail');
				if(catSuccess) {
					this.$emit("close");
				}
			});
		}
	},
	mounted() {
		let menuId = this.menuStore.getMenuIndex(localStorage.getItem("last_menu_id"));
		if(menuId) {
			this.changeMenu(menuId);
		}
	},
}
</script>

<style scoped lang="scss">
.printer-category-setting-container {
	.alignment-row {
		display: flex;
		justify-content: space-between;
		align-items: center;
		flex-wrap: wrap;
		width: 100%;
		gap: 10px;

		.right-element {
			margin-left: auto;
		}
	}
	.row-select-menu {
		border-radius: 13px;
		padding: 8px 12px;
		margin-bottom: 20px;
		background-color: var(--color-alpha-1);
	}
}
</style>